import {type QueryClient, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import type {
    BallotResource,
    BallotSummaryResource,
    PresenterState,
    SaveDraftBody,
    SaveResultsBody,
} from '../ballotTypes';
import {
    deleteRequest,
    getRequest,
    patchRequest,
    postRequest,
    putRequest,
} from '../utils/httpClient';

export type BallotAction = 'start' | 'extend' | 'end' | 'reveal/next' | 'reveal/back';

const refresh = (client: QueryClient, id: string) =>
    Promise.all([
        client.invalidateQueries({queryKey: ['ballot', id]}),
        client.invalidateQueries({queryKey: ['present', id]}),
        client.invalidateQueries({queryKey: ['ballots']}),
    ]);

export const useBallotList = (all: boolean) =>
    useQuery({
        queryKey: ['ballots', all],
        queryFn: () =>
            getRequest<BallotSummaryResource[]>(`/api/admin/ballots${all ? '?all=true' : ''}`),
    });

export const useBallot = (id: string) =>
    useQuery({
        queryKey: ['ballot', id],
        queryFn: () => getRequest<BallotResource>(`/api/admin/ballots/${id}`),
    });

export const useCreateBallot = () => {
    const client = useQueryClient();
    return useMutation({
        mutationFn: () => postRequest<BallotResource>('/api/admin/ballots'),
        onSuccess: (ballot) => {
            client.setQueryData(['ballot', ballot.id], ballot);
            return client.invalidateQueries({queryKey: ['ballots']});
        },
    });
};

export const useSaveDraft = (id: string) => {
    const client = useQueryClient();
    return useMutation({
        mutationKey: ['save-draft', id],
        scope: {id: `save-${id}`},
        mutationFn: (body: SaveDraftBody) =>
            putRequest<BallotResource>(`/api/admin/ballots/${id}`, body),
        onSuccess: (ballot) => client.setQueryData(['ballot', id], ballot),
    });
};

export const useSaveResults = (id: string) => {
    const client = useQueryClient();
    return useMutation({
        mutationKey: ['save-results', id],
        scope: {id: `save-${id}`},
        mutationFn: (body: SaveResultsBody) =>
            patchRequest<BallotResource>(`/api/admin/ballots/${id}/results`, body),
        onSuccess: (ballot) => {
            client.setQueryData(['ballot', id], ballot);
            return refresh(client, id);
        },
    });
};

export const useDuplicateBallot = () => {
    const client = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            postRequest<BallotResource>(`/api/admin/ballots/${id}/duplicate`),
        onSuccess: (_ballot, id) => refresh(client, id),
    });
};

export const useDeleteBallot = () => {
    const client = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => deleteRequest(`/api/admin/ballots/${id}`),
        onSuccess: (_unused, id) => refresh(client, id),
    });
};

export const useBallotAction = (id: string, action: BallotAction) => {
    const client = useQueryClient();
    return useMutation({
        mutationFn: () => postRequest<BallotResource>(`/api/admin/ballots/${id}/${action}`),
        onSuccess: () => refresh(client, id),
    });
};

export type PresenterSnapshot = {state: PresenterState; receivedAt: number};

export const usePresenterState = (id: string) =>
    useQuery({
        queryKey: ['present', id],
        staleTime: 0,
        queryFn: async (): Promise<PresenterSnapshot> => {
            const state = await getRequest<PresenterState>(`/api/admin/ballots/${id}/present`);
            return {state, receivedAt: Date.now()};
        },
    });
